import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LEARN_UI } from "./media/learnAssets";
import { usePrefersReducedMotion } from "./theme";

/**
 * Pip, the Learn companion — the transparent Pip animations the rest of the
 * site uses (public/assets/panda-clips: HEVC-alpha .mov for Safari, VP9-alpha
 * .webm for Chrome/Firefox). One clip per pose; a reaction pose plays for
 * `holdMs` and settles back to idle. Reduced motion / video failure falls back
 * to the still Pip art.
 *
 * `pose`: idle | think | cheer | oops   `mood`: default | writing (idle clip)
 */
// Every iPhone/iPad browser is WebKit underneath (Chrome "CriOS", Firefox "FxiOS"): they need the .mov too.
const IS_SAFARI = typeof navigator !== "undefined" && (/iP(hone|ad|od)/.test(navigator.userAgent)
  || /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent));

export const PIP_CLIPS = {
  idle: "panda_wave",
  writing: "panda_draw",
  think: "panda_point_2",
  cheer: "panda_jumping",
  oops: "panda_wave_heart",
  paint: "panda_paint",
  picture: "panda_picture",
};

const STILL = { idle: "pip_wave", writing: "pip_wave", think: "pip_think", cheer: "pip_cheer", oops: "pip_oops", paint: "pip_wave", picture: "pip_wave" };

export function pipClipSources(name) {
  const base = `/assets/panda-clips/${name}`;
  return IS_SAFARI
    ? [{ src: `${base}.mov`, type: 'video/mp4; codecs="hvc1"' }]
    : [{ src: `${base}.webm`, type: "video/webm" }];
}

export default function LearnCompanion({ size = 96, pose = "idle", mood = "default", holdMs = 2200, sticky = false, speaking = false, style, clipSequence }) {
  const reduceMotion = usePrefersReducedMotion();
  // legacy prop from the old shell: "correct" → cheer
  const effective = pose && pose !== "idle" ? pose : clipSequence === "correct" ? "cheer" : clipSequence === "wrong" ? "oops" : "idle";
  const [shown, setShown] = useState(effective);
  const [failed, setFailed] = useState(false);
  const timer = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    setShown(effective);
    clearTimeout(timer.current);
    if (effective !== "idle" && !sticky) timer.current = setTimeout(() => setShown("idle"), holdMs);
    return () => clearTimeout(timer.current);
  }, [effective, holdMs, sticky]);

  const clipKey = shown === "idle" && mood === "writing" ? "writing" : shown;
  const clip = PIP_CLIPS[clipKey] || PIP_CLIPS.idle;
  const sources = pipClipSources(clip);

  // Swap the clip without unmounting the element (keeps autoplay permission).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.load();
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, [clip]);

  const box = { width: size, height: size, position: "relative", flexShrink: 0, ...style };
  const shadow = "drop-shadow(0 8px 14px rgba(11,26,51,0.22))";

  if (reduceMotion || failed) {
    return (
      <div style={box} aria-label="Pip">
        <img src={LEARN_UI(STILL[clipKey] || "pip_wave")} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "contain", filter: shadow, pointerEvents: "none" }} />
      </div>
    );
  }

  return (
    <motion.div
      style={box}
      aria-label="Pip"
      animate={speaking ? { y: [0, -3, 0], transition: { repeat: Infinity, duration: 0.55 } } : { y: 0 }}
    >
      <motion.video
        key={clip}
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        onError={() => setFailed(true)}
        initial={shown === "idle" ? false : { scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 380, damping: 22 }}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", filter: shadow, pointerEvents: "none" }}
      >
        {sources.map((s) => <source key={s.src} src={s.src} type={s.type} />)}
      </motion.video>
    </motion.div>
  );
}

export const LearnCompanionLayout = { size: 96, headerSize: (compact) => (compact ? 88 : 96) };
